import { applyMiddleware, createStore } from 'redux';
import type { Action, Middleware, PreloadedState, Reducer, Store } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import { loggerMiddleware } from 'common/middleware/logger';

/**
 * Configures a redux store. For development, adds action logging, dev tools and a hook to bolt on
 * hot reload functionality.
 */
export default function configureStore<S, A extends Action>(options: {
  rootReducer: Reducer<S, A>;
  middlewares?: Array<Middleware<{}, S>>;
  hookToStore?: (store: Store<S, A>) => void;
  preloadedState?: S;
}): Store<S, A> {
  const middlewares = options.middlewares || [];
  if (__DEV__) {
    middlewares.push(loggerMiddleware);
  }

  const storeEnhancer = composeWithDevTools(applyMiddleware(...middlewares));
  let store: Store<S, A>;

  if (options.preloadedState) {
    store = createStore(
      options.rootReducer,
      // `PreloadedState<S>` is a conditional type and cannot reduce while `S`
      // is an unresolved type parameter, so it does not accept a plain `S`.
      options.preloadedState as PreloadedState<S>,
      storeEnhancer
    );
  } else {
    store = createStore(options.rootReducer, storeEnhancer);
  }

  if (options.hookToStore) {
    options.hookToStore(store);
  }

  return store;
}
